import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pill as PillIcon,
  ShoppingBag,
  Boxes,
  ShieldCheck,
  BarChart3,
  PackagePlus,
} from 'lucide-react';
import { Pill } from '@nako/ui';
import { isPharmacyBusiness } from '@nako/types';
import type { PharmacyBatch, PharmacyDrug } from '@nako/types';
import { stockOnHand, expiryStatus } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { supabase } from '@/lib/supabase';
import { DispensePOS } from '@/components/pharmacy/DispensePOS';
import { FormularyList } from '@/components/pharmacy/FormularyList';
import { BatchesExpiry } from '@/components/pharmacy/BatchesExpiry';
import { ControlledRegister } from '@/components/pharmacy/ControlledRegister';
import { PharmacyReports } from '@/components/pharmacy/PharmacyReports';

type Tab = 'dispense' | 'formulary' | 'batches' | 'register' | 'reports';

const TABS: Array<{ id: Tab; label: string; icon: typeof ShoppingBag }> = [
  { id: 'dispense', label: 'Dispense', icon: ShoppingBag },
  { id: 'formulary', label: 'Formulary', icon: PillIcon },
  { id: 'batches', label: 'Batches', icon: Boxes },
  { id: 'register', label: 'Register', icon: ShieldCheck },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

export interface PharmacyData {
  drugs: PharmacyDrug[];
  /** All non-deleted batches, keyed by drug_id. */
  batchesByDrug: Map<string, PharmacyBatch[]>;
  reload: () => Promise<void>;
  loading: boolean;
}

/**
 * Pulse Pharmacy — the pharmacist vertical.
 *
 * Gated to businesses whose industry is "Healthcare / Pharmacist". Presents a
 * segmented workspace: dispense (POS), formulary, batches/expiry, the
 * controlled-substances register, and reports. Drug + batch data is loaded
 * once here and shared with every tab so a dispense immediately reflects in
 * stock and low-stock counts.
 */
export default function Pharmacy() {
  const business = useAuthStore((s) => s.business)!;
  const profile = useAuthStore((s) => s.profile);
  const isPharmacy = isPharmacyBusiness(business.industry);

  const [tab, setTab] = useState<Tab>('dispense');
  const [drugs, setDrugs] = useState<PharmacyDrug[]>([]);
  const [batchesByDrug, setBatchesByDrug] = useState<Map<string, PharmacyBatch[]>>(new Map());
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    const [{ data: drugRows }, { data: batchRows }] = await Promise.all([
      supabase
        .from('pharmacy_drugs')
        .select('*')
        .eq('business_id', business.id)
        .is('deleted_at', null)
        .order('name'),
      supabase
        .from('pharmacy_batches')
        .select('*')
        .eq('business_id', business.id)
        .is('deleted_at', null)
        .order('expiry_date'),
    ]);
    setDrugs((drugRows ?? []) as PharmacyDrug[]);
    const map = new Map<string, PharmacyBatch[]>();
    for (const b of (batchRows ?? []) as PharmacyBatch[]) {
      const list = map.get(b.drug_id) ?? [];
      list.push(b);
      map.set(b.drug_id, list);
    }
    setBatchesByDrug(map);
    setLoading(false);
  }, [business.id]);

  useEffect(() => {
    if (!isPharmacy) {
      setLoading(false);
      return;
    }
    void reload();
  }, [isPharmacy, reload]);

  const data: PharmacyData = useMemo(
    () => ({ drugs, batchesByDrug, reload, loading }),
    [drugs, batchesByDrug, reload, loading],
  );

  // Headline counts for the hero strip.
  const { activeDrugs, lowStock, expiringSoon } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let low = 0;
    let expiring = 0;
    for (const d of drugs) {
      const batches = batchesByDrug.get(d.id) ?? [];
      if (stockOnHand(batches, { today }) <= d.reorder_level) low++;
      for (const b of batches) {
        const band = expiryStatus(b.expiry_date, today).band;
        if (b.qty_on_hand > 0 && (band === 'expired' || band === 'expiring_90')) {
          expiring++;
          break;
        }
      }
    }
    return { activeDrugs: drugs.length, lowStock: low, expiringSoon: expiring };
  }, [drugs, batchesByDrug]);

  if (!isPharmacy) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10 text-center">
        <PillIcon size={32} className="text-muted mx-auto mb-3 opacity-50" />
        <h1 className="font-display mb-1 text-xl">Pulse Pharmacy</h1>
        <p className="text-muted text-sm">
          This module is available for pharmacist practices. Set your industry to &ldquo;Healthcare
          / Pharmacist&rdquo; in Settings to enable dispensing, stock batches, the
          controlled-substances register and medical-aid claims.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-4 md:px-6 md:py-6">
      {/* Hero strip */}
      <div className="relative mb-5 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50 p-5 [animation:hero-in_0.55s_cubic-bezier(0.22,1,0.36,1)_both]">
        <div
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-30 blur-3xl"
          style={{ background: '#34D399' }}
        />
        <div className="relative flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/80 shadow-[0_8px_28px_rgba(16,185,129,0.18)] ring-1 ring-emerald-200">
            <PillIcon size={30} className="text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-ink text-xl">Pulse Pharmacy</h1>
              <Pill tone="green">{activeDrugs} drugs</Pill>
              {lowStock > 0 && <Pill tone="accent">{lowStock} low stock</Pill>}
              {expiringSoon > 0 && <Pill tone="red">{expiringSoon} expiring</Pill>}
            </div>
            <p className="text-muted mt-1 text-sm">
              Dispense, stock, controlled register &amp; medical-aid — {business.name}
            </p>
          </div>
        </div>
      </div>

      {/* Segmented tab bar */}
      <div className="-mx-1 mb-5 flex gap-1 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                'rounded-btn flex shrink-0 items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ' +
                (active ? 'bg-ink text-paper' : 'bg-paper-2 text-muted hover:text-ink')
              }
            >
              <Icon size={16} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'dispense' && <DispensePOS business={business} profile={profile} data={data} />}
      {tab === 'formulary' && <FormularyList business={business} profile={profile} data={data} />}
      {tab === 'batches' && <BatchesExpiry business={business} profile={profile} data={data} />}
      {tab === 'register' && <ControlledRegister business={business} data={data} />}
      {tab === 'reports' && <PharmacyReports business={business} data={data} />}

      <p className="text-muted mt-8 flex items-start gap-1.5 text-xs">
        <PackagePlus size={13} className="mt-0.5 shrink-0" />
        The in-app controlled register supplements — it does not replace — any statutory physical
        register required by BoMRA. Confirm VAT treatment of medicines with BURS / your accountant.
      </p>
    </main>
  );
}
